/**
 * The result card's geometry and its motion set — the whole of both, in one
 * place, because two things depend on them and neither can be allowed to drift.
 *
 * **Geometry** is shared with `ListingCardSkeleton`. A placeholder whose box is
 * a *copy* of the card's box is a promise that the two stay equal; the promise
 * is what lets a page jump under a thumb the day someone changes `p-4` to `p-5`
 * on one of them. Here the padding, the photo aspect and every text row are one
 * string each, applied by both components, so the placeholder cannot disagree
 * with the card about how tall the card is. (`docs/verification-playbook.md`
 * §12a: a predicate that appears once cannot disagree with itself.)
 *
 * **Motion** is every animated class the card applies, and every one of them is
 * behind `motion-safe:`, which Tailwind compiles to
 * `@media (prefers-reduced-motion: no-preference)`. That makes "is anything
 * still moving for someone who asked for less motion?" a question about the six
 * strings below rather than about a 350-line component, and
 * `ListingCard.motion.test.tsx` answers it by compiling these strings with the
 * real Tailwind compiler and walking the CSS it emits.
 *
 * What is deliberately NOT gated: colour and shadow changes on hover. They are
 * affordances, not movement, and taking the hover feedback away from people who
 * asked for less animation gives them less *information*, which is not the same
 * request. The global rule in `globals.css` already collapses their transition
 * to 0.01ms under reduce, so they snap rather than animate.
 */

import type { CSSProperties } from 'react';

/**
 * **Every class name below is written out in full, and none of it may ever be
 * built by interpolation.**
 *
 * Tailwind generates CSS by scanning the *source text* for candidates. It does
 * not run the file. `` `duration-[${LIFT_MS}ms]` `` is, to the scanner, the
 * string `duration-[${LIFT_MS}ms]` — not a class — so the rule is never
 * emitted and the class on the element matches nothing at all.
 *
 * This is not hypothetical: the first version of this file was written that
 * way. Every unit test passed, because a unit test compiles the string the
 * component produces at RUNTIME. A real browser, given the CSS the real build
 * produces, showed `translate: none` on hover — the lift, the photo push and
 * the heart's easing were all silently absent while sixteen green tests said
 * otherwise. `ListingCard.motion.test.tsx` now runs Tailwind's own extractor
 * over this file and fails if a class here is not something it can find.
 *
 * The numbers below are therefore read back OUT of the class names rather than
 * substituted into them: one statement of each value, and the constants cannot
 * drift from the CSS that actually ships.
 */
export const CARD_MOTION = {
  /**
   * The transition itself: what animates, for how long, on which curve. Split
   * from the hover state so the resting card carries the timing and the hover
   * carries only the destination — a card that gains its transition *on* hover
   * animates in and snaps out.
   */
  frame:
    'motion-safe:transition-[translate,box-shadow,border-color] ' +
    'motion-safe:duration-[340ms] motion-safe:ease-[cubic-bezier(.16,1,.3,1)]',

  /** …and where it goes: 5px up under the pointer. */
  liftHover: 'motion-safe:hover:-translate-y-[5px]',

  /**
   * The same 5px, held, when the map hovers or selects this home. The list and
   * the map stay in lock-step, so the card the pin points at must read as
   * lifted without a pointer on it.
   */
  liftActive: 'motion-safe:-translate-y-[5px]',

  /** The photo pushes ~3% while the frame holds still. */
  photo:
    'motion-safe:transition-transform motion-safe:duration-[340ms] ' +
    'motion-safe:ease-[cubic-bezier(.16,1,.3,1)] motion-safe:group-hover:scale-[1.03]',

  /**
   * The sheen: a soft diagonal highlight over the photograph, whose position
   * follows the pointer (see `SHEEN_STYLE`). Two independent stops under
   * reduce — it never fades in, and it is not rendered at all — because this is
   * the one item in the set that moves *continuously* rather than between two
   * states, and a transition-duration override would not still it.
   *
   * No rotation and no perspective anywhere near it: the 3D tilt this arrived
   * with was rejected, and a `rotate`/`perspective` declaration on the card is a
   * regression, not a variation.
   */
  sheen:
    'opacity-0 motion-safe:transition-opacity motion-safe:duration-[340ms] ' +
    'motion-safe:ease-[cubic-bezier(.16,1,.3,1)] motion-safe:group-hover:opacity-100 ' +
    'motion-reduce:hidden',

  /**
   * The save control gives under the press and springs back past its resting
   * size. `scale`, not `transform`, so it composes with anything else on the
   * button.
   */
  heart:
    'motion-safe:transition-[scale,background-color] motion-safe:duration-[340ms] ' +
    'motion-safe:ease-[cubic-bezier(.34,1.56,.64,1)] motion-safe:active:scale-90',
} as const;

/** Read a value back out of a class name, loudly if it is not there. */
function readBack(from: string, pattern: RegExp, what: string): string {
  const m = from.match(pattern);
  if (!m) throw new Error(`ListingCard.tokens: cannot read ${what} out of "${from}"`);
  return m[1];
}

/** How far the card rises under the pointer, in px. */
export const LIFT_PX = Number(
  readBack(CARD_MOTION.liftHover, /-translate-y-\[(\d+(?:\.\d+)?)px\]/, 'the lift distance')
);

/** How long the lift takes, in ms. Also the sheen fade and the heart's spring. */
export const LIFT_MS = Number(
  readBack(CARD_MOTION.frame, /duration-\[(\d+)ms\]/, 'the lift duration')
);

/** The system's easing. Not invented here — it is the one in `--animate-fade-up`. */
export const EASE_SYSTEM = readBack(
  CARD_MOTION.frame,
  /ease-\[(cubic-bezier\([^\]]*\))\]/,
  'the system easing'
);

/**
 * The overshoot curve, for the save control only. Its second control point sits
 * past 1, so releasing the heart carries it beyond its resting size and back —
 * a save that feels committed rather than merely registered.
 */
export const EASE_OVERSHOOT = readBack(
  CARD_MOTION.heart,
  /ease-\[(cubic-bezier\([^\]]*\))\]/,
  'the overshoot easing'
);

/** How much the photo pushes inside its frame, so the frame reads as a window. */
export const PHOTO_PUSH = Number(
  readBack(CARD_MOTION.photo, /scale-\[(\d+(?:\.\d+)?)\]/, 'the photo push')
);

/**
 * The sheen's paint. A module constant, not a computed object: it is emitted
 * identically on the server and the client, so it cannot produce a hydration
 * mismatch, and the two custom properties it reads are set on the photo frame by
 * the pointer handler with fallbacks here, so it renders correctly before any
 * pointer has ever moved.
 *
 * White at 30% rather than a palette colour: this is specular light on a
 * photograph, and an ink-tinted highlight reads as dirt on the glass. The photo
 * chrome around it (`bg-white/60` dots, white counters) is already white.
 */
export const SHEEN_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(105deg, transparent 38%, rgb(255 255 255 / 30%) 50%, transparent 62%)',
  backgroundSize: '260% 260%',
  backgroundPosition: 'var(--sheen-x, 50%) var(--sheen-y, 50%)',
};

/**
 * Every box that decides how tall the card is. `ListingCardSkeleton` lays out
 * the same list, so the placeholder's height is the card's height by
 * construction rather than by measurement.
 *
 * The text rows carry font size and leading, and the skeleton's bars are
 * `h-[1lh]` inside them — one line box of whatever type that row is set in. So
 * changing `text-xl` to `text-2xl` on the price moves the placeholder too,
 * without anyone remembering to.
 */
export const CARD_GEOMETRY = {
  /** The outer frame. The card adds `group` and its border colour. */
  frame: 'block overflow-hidden rounded-card border bg-surface/80',
  /** The photo box. Passed to `PhotoCarousel` explicitly so it is not a default. */
  photo: 'aspect-[4/3]',
  body: 'p-4',
  priceRow: 'mb-2 flex flex-wrap items-baseline justify-between gap-x-2',
  price: 'flex items-baseline gap-2 text-xl font-bold tabular-nums text-ink',
  ppsf: 'text-sm tabular-nums text-ink-muted',
  facts: 'mb-2 flex items-center gap-4 text-ink-muted',
  address: 'truncate font-medium text-ink-soft',
  locality: 'truncate text-sm text-ink-muted',
  age: 'mt-2 text-xs text-ink-faint',
} as const;

/**
 * The save control's accessible name.
 *
 * It is a toggle, so it also carries `aria-pressed`, and a screen reader
 * announces the state from that. The name changes as well because the name is
 * what a voice-control user says out loud and what a switch user reads in a
 * list: "Saved" and "Save this home" are the two things this button can be, and
 * a name that never changes ("Favorite") leaves them guessing which.
 */
export const SAVE_LABEL = {
  idle: 'Save this home',
  saved: 'Saved',
} as const;
